import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import POLookup from './components/POLookup';
import POImport from './components/POImport';
import POSubmitted from './components/POSubmitted';
import APFSSubmitted from './components/APFSSubmitted';
import APFSDashboard from './components/APFSDashboard';
import PaymentLookup from './components/PaymentLookup';
import POImportResults from './components/POImportResults';
import Login from './components/Login';
import ContractStatus from './components/ContractStatus';
import PolicySubmit from './components/PolicySubmit';
import AdminMappings from './components/AdminMappings';
import LMSStatus from './components/LMSStatus';
import PolicyMISData from './components/PolicyMISData';
import EODReport from './components/EODReport';
import RoleAdmin from './components/RoleAdmin';
import { AuthProvider, useAuth } from './context/AuthContext';
import API_BASE_URL from './config';

// Tab → provider grouping. Drives both provider row and sub-tab row.
const TAB_GROUPS = {
  ame: [
    { key: 'lookup', label: 'PO Lookup' },
    { key: 'contract-status', label: 'Contract Status' },
    { key: 'submitted', label: 'PO Submitted' },
    { key: 'payment', label: 'Payment Lookup' },
  ],
  apfs: [
    { key: 'apfs-dashboard', label: 'Dashboard' },
    { key: 'policy-submit', label: 'Policy Submit' },
    { key: 'apfs-submitted', label: 'APFS Submitted' },
    { key: 'admin-mappings', label: 'APFS Mappings' },
    { key: 'lms-status', label: 'L.11 LMS Status' },
    { key: 'policy-mis', label: 'L.13 Policy MIS' },
    { key: 'eod-report', label: 'EOD Report' },
  ],
  admin: [
    { key: 'role-admin', label: 'Roles', adminOnly: true },
  ],
};

const PROVIDER_LABELS = { ame: 'AME', apfs: 'APFS', admin: 'Admin' };

function AppContent() {
  const { user, isAuthenticated, logout, loading, loginWithToken, can } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Tab groups visible to the current user: admin-only tabs require admin, and
  // every other tab requires the matching view capability (can(tabKey,'view')).
  const visibleGroups = React.useMemo(() => {
    const out = {};
    Object.entries(TAB_GROUPS).forEach(([provider, tabs]) => {
      const allowed = tabs.filter((t) => (t.adminOnly ? isAdmin : can(t.key, 'view')));
      if (allowed.length) out[provider] = allowed;
    });
    return out;
  }, [isAdmin, can]);

  const PROVIDER_OF_TAB = React.useMemo(() => Object.fromEntries(
    Object.entries(visibleGroups).flatMap(([provider, tabs]) =>
      tabs.map((t) => [t.key, provider])
    )
  ), [visibleGroups]);

  const [activeTab, setActiveTab] = useState('lookup');
  const activeProvider = PROVIDER_OF_TAB[activeTab] || 'ame';

  // If the current tab isn't visible to this user (permissions changed, or a
  // deep-linked admin tab), fall back to the first tab they can see.
  React.useEffect(() => {
    const visibleKeys = Object.values(visibleGroups).flat().map((t) => t.key);
    if (visibleKeys.length && !visibleKeys.includes(activeTab)) {
      setActiveTab(visibleKeys[0]);
    }
  }, [visibleGroups, activeTab]);

  const handleProviderSwitch = (provider) => {
    if (provider === activeProvider) return;
    setActiveTab(visibleGroups[provider][0].key);
  };
  const [lookupResults, setLookupResults] = useState(null);
  const [environment, setEnvironment] = useState(null);
  const [envSwitching, setEnvSwitching] = useState(false);
  const [autoLookupLoading, setAutoLookupLoading] = useState(false);
  const [autoLookupError, setAutoLookupError] = useState('');

  // Store URL parameters immediately on mount (before they might get cleared)
  const [pendingPO, setPendingPO] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('po');
  });
  // apfs_po → switch to APFS > policy-submit and pre-fill PO
  const [pendingApfsPO, setPendingApfsPO] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('apfs_po');
  });
  // apfs_initial_po passes down to PolicySubmit for auto-lookup
  const [apfsInitialPO, setApfsInitialPO] = useState(null);

  // Handle ?token= URL param: store JWT token so user auto-authenticates
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken && !isAuthenticated) {
      loginWithToken(urlToken).then(() => {
        const url = new URL(window.location);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url);
      });
    } else if (urlToken) {
      const url = new URL(window.location);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear PO from URL without page reload
  const clearURLPOParam = () => {
    const url = new URL(window.location);
    url.searchParams.delete('po');
    window.history.replaceState({}, '', url);
  };

  // Validate PO format: PO + 8 digits
  const validatePOFormat = (value) => {
    const pattern = /^PO\d{8}$/i;
    return pattern.test(value.trim());
  };

  // Auto-lookup PO from URL parameter after authentication
  useEffect(() => {
    const autoLookupPO = async () => {
      // Skip if still loading auth, no pending PO, or not authenticated
      if (loading || !pendingPO || !isAuthenticated) {
        console.log('Auto-lookup skip:', { loading, pendingPO, isAuthenticated });
        return;
      }

      console.log('Auto-lookup starting for PO:', pendingPO);

      // Clear pending PO to prevent re-lookup
      const poToLookup = pendingPO;
      setPendingPO(null);
      clearURLPOParam();

      // Validate PO format
      const trimmedPO = poToLookup.trim().toUpperCase();
      if (!validatePOFormat(trimmedPO)) {
        setAutoLookupError('รูปแบบ PO ไม่ถูกต้อง (ต้องเป็น PO ตามด้วยตัวเลข 8 หลัก เช่น PO68005946)');
        return;
      }

      // Set tab to lookup and start loading
      setActiveTab('lookup');
      setAutoLookupLoading(true);
      setAutoLookupError('');

      try {
        const response = await axios.post(`${API_BASE_URL}/api/po/lookup`, {
          po_number: trimmedPO
        });

        if (response.data.success) {
          setLookupResults(response.data);
          console.log('Auto-lookup success:', trimmedPO);
        } else {
          setAutoLookupError(response.data.error || 'ไม่พบข้อมูล PO');
        }
      } catch (err) {
        console.error('Auto-lookup error:', err);
        setAutoLookupError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
      } finally {
        setAutoLookupLoading(false);
      }
    };

    autoLookupPO();
  }, [loading, isAuthenticated, pendingPO]);

  // Auto-navigate to APFS policy-submit from ?apfs_po= URL param
  useEffect(() => {
    if (loading || !pendingApfsPO || !isAuthenticated) return;

    const trimmedPO = pendingApfsPO.trim().toUpperCase();
    setPendingApfsPO(null);

    const url = new URL(window.location);
    url.searchParams.delete('apfs_po');
    window.history.replaceState({}, '', url);

    setActiveTab('policy-submit');
    setApfsInitialPO(trimmedPO);
  }, [loading, isAuthenticated, pendingApfsPO]);

  // Fetch environment status
  useEffect(() => {
    const fetchEnvironment = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/config/environment`);
        if (response.data.success) {
          setEnvironment(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch environment:', err);
      }
    };

    if (isAuthenticated) {
      fetchEnvironment();
    }
  }, [isAuthenticated]);

  // Switch environment
  const handleEnvSwitch = async () => {
    if (envSwitching || !environment) return;

    const newEnv = environment.isProduction ? 'uat' : 'production';
    const confirmMsg = `Switch to ${newEnv.toUpperCase()}?\n\nThis will change the AME API endpoint for all operations.`;

    if (!window.confirm(confirmMsg)) return;

    setEnvSwitching(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/config/environment`, {
        environment: newEnv
      });
      if (response.data.success) {
        setEnvironment(response.data);
      }
    } catch (err) {
      console.error('Failed to switch environment:', err);
      alert('Failed to switch environment: ' + (err.response?.data?.error || err.message));
    } finally {
      setEnvSwitching(false);
    }
  };

  const handlePODataLoaded = (data) => {
    // data now has processedRows format (same as Google Sheet import)
    setLookupResults(data);
  };

  const handleLookupClose = () => {
    setLookupResults(null);
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="App">
        <div className="app-loading">Loading...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // If Contract Status is active, render it full screen (or close to it)
  // We provide a way to go back by checking activeTab
  if (activeTab === 'contract-status') {
    return (
      <div className="relative">
        {/* Simple back button overlay if needed, or we rely on the user refreshing/using browser nav if we pushed state. 
             But since we don't push state, let's wrap it or add a back button. 
             ContractStatus has a sidebar. We can add a "Back to Main Apps" button there if we could edit it easily, 
             but for now let's just render it. 
             To be safe, let's render a Floating Action Button or similar to go back
         */}
        <button
          onClick={() => setActiveTab('lookup')}
          className="fixed top-4 right-4 z-50 bg-white text-slate-800 px-4 py-2 rounded-lg shadow-lg font-bold border border-slate-200 hover:bg-slate-50"
        >
          Back to AME
        </button>
        <ContractStatus />
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-title">
            <div className="title-row">
              <h1>AME API Integration Platform</h1>
              {/* UAT/Prod toggle - admins only */}
              {environment && isAdmin && (
                <div className="env-switch-container">
                  <span className="env-label">UAT</span>
                  <button
                    className={`env-toggle ${environment.isProduction ? 'env-toggle-prod' : 'env-toggle-uat'}`}
                    onClick={handleEnvSwitch}
                    disabled={envSwitching}
                    title={`Click to switch to ${environment.isProduction ? 'UAT' : 'Production'}`}
                  >
                    <span className="env-toggle-slider"></span>
                  </button>
                  <span className="env-label">PROD</span>
                </div>
              )}
            </div>
            <p>Query PO Details and Submit to AME Insurance API</p>
          </div>
          <div className="header-user">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="user-avatar" />
            )}
            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button className="btn-logout" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        {/* Provider selector (top-level) */}
        <div className="provider-tabs" role="tablist" aria-label="Provider">
          {Object.keys(visibleGroups).map((provider) => (
            <button
              key={provider}
              type="button"
              role="tab"
              aria-selected={activeProvider === provider}
              className={`provider-tab ${activeProvider === provider ? 'active' : ''}`}
              onClick={() => handleProviderSwitch(provider)}
            >
              {PROVIDER_LABELS[provider] || provider}
            </button>
          ))}
        </div>

        {/* Action sub-tabs for the selected provider */}
        <div className="main-tabs" role="tablist" aria-label={`${activeProvider} actions`}>
          {(visibleGroups[activeProvider] || []).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`main-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'import' && <POImport />}

        {activeTab === 'submitted' && <POSubmitted />}

        {activeTab === 'apfs-dashboard' && <APFSDashboard />}

        {activeTab === 'apfs-submitted' && <APFSSubmitted />}

        {activeTab === 'payment' && <PaymentLookup />}

        {activeTab === 'policy-submit' && <PolicySubmit initialPO={apfsInitialPO} onInitialPOConsumed={() => setApfsInitialPO(null)} />}

        {activeTab === 'admin-mappings' && <AdminMappings />}

        {activeTab === 'lms-status' && <LMSStatus />}

        {activeTab === 'policy-mis' && <PolicyMISData />}

        {activeTab === 'eod-report' && <EODReport />}

        {activeTab === 'role-admin' && isAdmin && <RoleAdmin />}

        {activeTab === 'lookup' && (
          <>
            <POLookup onDataLoaded={handlePODataLoaded} />

            {/* Auto-lookup loading indicator */}
            {autoLookupLoading && (
              <div className="auto-lookup-loading">
                <div className="loading-spinner"></div>
                <p>กำลังค้นหา PO จาก URL...</p>
              </div>
            )}

            {/* Auto-lookup error message */}
            {autoLookupError && (
              <div className="auto-lookup-error">
                <span className="error-icon">❌</span>
                <span>{autoLookupError}</span>
                <button onClick={() => setAutoLookupError('')} className="dismiss-btn">✕</button>
              </div>
            )}

            {lookupResults && (
              <POImportResults
                results={lookupResults}
                onClose={handleLookupClose}
              />
            )}
          </>
        )}
      </div>

      <footer className="App-footer">
        <p>Powered by Click Insurance Broker Co.,Ltd.</p>
      </footer>
    </div>
  );
}
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
